import * as React from 'react';
import './LexicalRichTextareaEditor.css';
import { $getRoot, $createParagraphNode, $createTextNode, $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND, ElementNode, TextFormatType, ElementFormatType } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ParagraphNode, TextNode } from 'lexical';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { $createListItemNode, $createListNode } from '@lexical/list';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import type { HeadingTagType } from '@lexical/rich-text';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';

type EditorProps = {
  editorState: string;
  onChangeDescription: (html: string) => void;
  id?: string;
  placeholder?: string;
  minHeight?: string;
}

const theme = {
  paragraph: 'editor-paragraph',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
  },
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
  },
  quote: 'editor-quote',
  list: {
    nested: {
      listitem: 'editor-nested-listitem',
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem',
  },
  code: 'editor-code',
  table: 'editor-table',
  tableCell: 'editor-table-cell',
  tableRow: 'editor-table-row',
};

function onError(error: Error) {
  console.error(error);
}

const sanitizeHtml = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;

  // Remove Lexical classes from elements
  const elements = div.getElementsByTagName('*');
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    element.removeAttribute('class');
    element.removeAttribute('style');
  }

  // Remove links (convert to plain text)
  const links = div.querySelectorAll('a');
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    const text = document.createTextNode(link.textContent || '');
    if (link.parentNode) {
      link.parentNode.replaceChild(text, link);
    }
  }

  return div.innerHTML;
};

export const LexicalRichTextareaEditor: React.FC<EditorProps> = ({
  editorState,
  onChangeDescription,
  id,
  placeholder = "Enter description...",
  minHeight = "150px"
}) => {
  const initialConfig = {
    namespace: 'DDSJobsEditor',
    theme,
    onError,
    nodes: [
      ParagraphNode,
      TextNode,
      HeadingNode,
      QuoteNode,
      ListItemNode,
      ListNode,
      CodeHighlightNode,
      CodeNode,
      TableCellNode,
      TableNode,
      TableRowNode,
      LinkNode,
      AutoLinkNode
    ],
    editorState: (editor) => {
      editor.update(() => {
        if (!editorState || editorState.trim() === '') {
          const root = $getRoot();
          const paragraph = $createParagraphNode();
          paragraph.append($createTextNode(''));
          root.append(paragraph);
          return;
        }

        try {
          const parser = new DOMParser();
          const dom = parser.parseFromString(editorState, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();
          nodes.forEach(node => root.append(node));
        } catch (error) {
          console.error('Error parsing HTML:', error);
          const root = $getRoot();
          const paragraph = $createParagraphNode();
          paragraph.append($createTextNode(editorState));
          root.clear();
          root.append(paragraph);
        }
      });
    },
  };

  return (
    <div className="editor-container" style={{ width: '100%' }}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className="editor-inner">
          <ToolbarPlugin />
          {/* Positioning context for the absolute placeholder: without this it
              resolves against an ancestor that INCLUDES the toolbar, so
              "Enter description…" rendered on top of the toolbar buttons. */}
          <div style={{ position: 'relative' }}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className="editor-input"
                  style={{ minHeight }}
                  id={id}
                />
              }
              placeholder={<div className="editor-placeholder">{placeholder}</div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <ListPlugin />
          <OnChangePlugin onChange={(editorState, editor) => {
            editorState.read(() => {
              try {
                const htmlString = $generateHtmlFromNodes(editor);
                if (htmlString) {
                  const cleanHtml = sanitizeHtml(htmlString);
                  onChangeDescription(cleanHtml);
                } else {
                  onChangeDescription('');
                }
              } catch (error) {
                console.error('Error generating HTML:', error);
                onChangeDescription('');
              }
            });
          }} />
        </div>
      </LexicalComposer>
    </div>
  );
};

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();

  const formatText = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatElement = (blockType: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (blockType === 'ul' || blockType === 'ol') {
        const parentList = selection.anchor.getNode().getParent();

        if (parentList?.getType() === 'list') {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          if (blockType === 'ul') {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          }
        }
      } else if (blockType === 'quote') {
        const quote = $createQuoteNode();
        quote.append($createTextNode(selection.getTextContent()));
        selection.insertNodes([quote]);
      } else if (blockType.startsWith('h')) {
        const tag = blockType as HeadingTagType;
        if (['h1', 'h2', 'h3'].includes(tag)) {
          const heading = $createHeadingNode(tag);
          heading.append($createTextNode(selection.getTextContent()));
          selection.insertNodes([heading]);
        }
      } else if (['left', 'center', 'right', 'justify'].includes(blockType)) {
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, blockType as ElementFormatType);
      }
    });
  };

  return (
    <div className="toolbar" role="toolbar">
      <div className="btn-group" role="group">
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatText('bold'); }}>
          <i className="fas fa-bold"></i>
        </button>
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatText('italic'); }}>
          <i className="fas fa-italic"></i>
        </button>
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatText('underline'); }}>
          <i className="fas fa-underline"></i>
        </button>
      </div>

      <div className="btn-group" role="group">
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatElement('h1'); }}>
          <i className="fas fa-heading">1</i>
        </button>
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatElement('h2'); }}>
          <i className="fas fa-heading">2</i>
        </button>
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatElement('h3'); }}>
          <i className="fas fa-heading">3</i>
        </button>
      </div>

      <div className="btn-group" role="group">
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatElement('ul'); }}>
          <i className="fas fa-list-ul"></i>
        </button>
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatElement('ol'); }}>
          <i className="fas fa-list-ol"></i>
        </button>
      </div>

      <div className="btn-group" role="group">
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatElement('quote'); }}>
          <i className="fas fa-quote-right"></i>
        </button>
        <button type="button" className="btn" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.preventDefault(); formatText('code'); }}>
          <i className="fas fa-code"></i>
        </button>
      </div>
    </div>
  );
}

export default LexicalRichTextareaEditor;
